import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "@/components/AdminShell";
import { useAuth } from "@/lib/auth";
import { Trash2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeads() {
  const { authHeader } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { headers: authHeader });
      setLeads(data);
    } catch (e) {
      toast.error("Could not load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers: authHeader });
      setLeads((l) => l.filter((x) => x.id !== id));
      toast.success("Lead removed.");
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  return (
    <AdminShell title="Leads">
      <div data-testid="admin-leads-list" className="border border-[var(--border-dim)] rounded-sm overflow-hidden bg-[var(--bg-2)]">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[var(--border-dim)] font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-3)]">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Project</div>
          <div className="col-span-3">Received</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {loading && (
          <div className="px-5 py-10 text-center font-mono-ui text-[11px] tracking-[0.22em] uppercase text-[var(--text-3)]">
            Loading...
          </div>
        )}

        {!loading && leads.length === 0 && (
          <div data-testid="admin-leads-empty" className="px-5 py-14 text-center">
            <Mail className="w-6 h-6 mx-auto text-[var(--text-3)] mb-3" />
            <div className="font-display text-lg">No leads yet</div>
            <div className="overline mt-2">They will appear here as visitors submit briefs.</div>
          </div>
        )}

        {!loading && leads.map((l) => {
          const isOpen = open === l.id;
          return (
            <div
              key={l.id}
              data-testid={`admin-lead-row-${l.id}`}
              className="border-b border-[var(--border-dim)] last:border-b-0"
            >
              <div
                onClick={() => setOpen(isOpen ? null : l.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(isOpen ? null : l.id); } }}
                className="cursor-pointer grid grid-cols-12 gap-4 px-5 py-4 hover:bg-[var(--surface)]/40 transition-colors items-center"
              >
                <div className="col-span-3 font-display font-medium">{l.name}</div>
                <div className="col-span-3 font-mono-ui text-xs text-[var(--text-2)] truncate">{l.email}</div>
                <div className="col-span-2">
                  {l.project_type ? (
                    <span className="chip">{l.project_type}</span>
                  ) : (
                    <span className="font-mono-ui text-[10px] text-[var(--text-3)]">—</span>
                  )}
                </div>
                <div className="col-span-3 font-mono-ui text-[11px] text-[var(--text-3)]">
                  {new Date(l.created_at).toLocaleString()}
                </div>
                <div className="col-span-1 flex justify-end">
                  <button
                    data-testid={`admin-lead-delete-${l.id}`}
                    onClick={(e) => { e.stopPropagation(); remove(l.id); }}
                    className="text-[var(--text-3)] hover:text-[var(--vermilion)] transition-colors p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="px-5 pb-6 pt-1 grid grid-cols-12 gap-6">
                  <div className="col-span-12 md:col-span-8 border border-[var(--border-dim)] bg-[var(--bg)] rounded-sm p-5">
                    <div className="overline mb-2">Message</div>
                    <p className="text-[var(--text-1)] leading-relaxed whitespace-pre-wrap">{l.message}</p>
                  </div>
                  <div className="col-span-12 md:col-span-4 space-y-3 font-mono-ui text-xs">
                    <div>
                      <div className="overline">Phone</div>
                      <div className="mt-1">
                        {l.phone ? (
                          <a href={`tel:${l.phone}`} className="hover:text-[var(--lime)]">{l.phone}</a>
                        ) : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="overline">Company</div>
                      <div className="mt-1">{l.company || "—"}</div>
                    </div>
                    <a
                      href={`mailto:${l.email}`}
                      className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-[var(--vermilion)] hover:bg-[var(--vermilion-2)] text-white rounded-full text-[11px] tracking-wider"
                    >
                      Reply via email
                    </a>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AdminShell>
  );
}
